const MODULE_RELOAD_ATTEMPTS_KEY = "nitfisio:module-reload-attempts";
const LEGACY_MODULE_RELOAD_ATTEMPTED_KEY = "nitfisio:module-reload-attempted";
const MODULE_RELOAD_QUERY_PARAM = "module-reload-attempt";
const MAX_MODULE_RELOAD_ATTEMPTS = 2;
const MODULE_RECOVERY_RESET_DELAY_MS = 15_000;

/**
 * Only match explicit module-import failure messages.
 * Do NOT match generic chunk URLs — those appear in normal stack traces too.
 */
const MODULE_FAILURE_PATTERN =
  /importing a module script failed|failed to fetch dynamically imported module|error loading dynamically imported module/i;

function readFailureText(value: unknown) {
  if (typeof value === "string") return value;
  if (value instanceof Error) return value.message || "";
  return "";
}

function readRecoveryAttempts() {
  if (typeof window === "undefined") return 0;
  try {
    return Number.parseInt(sessionStorage.getItem(MODULE_RELOAD_ATTEMPTS_KEY) || "0", 10);
  } catch {
    return 0;
  }
}

export function hasExceededModuleImportRecoveryAttempts() {
  return readRecoveryAttempts() >= MAX_MODULE_RELOAD_ATTEMPTS;
}

export function isPreviewOrIframeContext() {
  if (typeof window === "undefined") return false;

  const host = window.location.hostname.toLowerCase();

  try {
    if (window.self !== window.top) return true;
  } catch {
    return true;
  }

  return host.includes("id-preview--") || host.includes("lovableproject.com");
}

/**
 * Detect real module-import failures.
 * Only matches the explicit error message — NOT generic chunk filenames.
 */
export function isModuleImportFailure(value: unknown) {
  if (typeof value !== "string" && !(value instanceof Error)) return false;

  const message = readFailureText(value);
  return MODULE_FAILURE_PATTERN.test(message);
}

export function resetModuleImportRecoveryState() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(MODULE_RELOAD_ATTEMPTS_KEY);
    sessionStorage.removeItem(LEGACY_MODULE_RELOAD_ATTEMPTED_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Schedule a delayed reset so the counter survives the initial page load
 * and only clears after the app has stabilised.
 */
export function scheduleModuleImportRecoveryStateReset() {
  if (typeof window === "undefined") return;
  window.setTimeout(() => {
    resetModuleImportRecoveryState();
  }, MODULE_RECOVERY_RESET_DELAY_MS);
}

async function clearModuleCaches() {
  if (typeof window === "undefined") return;
  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    }
    if ("caches" in window) {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map((k) => caches.delete(k)));
    }
  } catch {
    // Best effort
  }
}

export async function cleanupPreviewModuleCaches() {
  if (!isPreviewOrIframeContext()) return;
  await clearModuleCaches();
}

export async function recoverFromModuleImportFailure() {
  if (typeof window === "undefined") return false;

  const attempts = readRecoveryAttempts();
  if (attempts >= MAX_MODULE_RELOAD_ATTEMPTS) return false;

  try {
    sessionStorage.setItem(MODULE_RELOAD_ATTEMPTS_KEY, String(attempts + 1));
  } catch {
    // Ignore
  }

  await clearModuleCaches();

  try {
    const url = new URL(window.location.href);
    url.searchParams.set("v", Date.now().toString());
    url.searchParams.set(MODULE_RELOAD_QUERY_PARAM, String(attempts + 1));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }

  return true;
}
